import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { get } from "../api";
import type { BillingPlanRow, CustomerRevenue as Page, MonthFigures, Plan, RevenueLine, RevenueMonthDetail } from "../api";
import { useAuth } from "../auth";
import { Time } from "../Time";
import BriefCard from "../revenue/BriefCard";
import PaceCard from "../revenue/PaceCard";
import { StatusPill } from "../revenue/StatusPill";
import {
  describePulse,
  minutes,
  money,
  monthLabel,
  monthParam,
  rate,
  ruleName,
  threshold,
  vmShare,
} from "../revenue/format";
import { useCustomer } from "./CustomerLayout";

/**
 * One customer's revenue: seven months, then the picked month in full.
 *
 * The month in full is fetched on its own, when it is picked -- the line
 * items are per campaign and per kind, and most visits only want the
 * table above them.
 */
export default function CustomerRevenue() {
  const { customer } = useCustomer();
  const { me } = useAuth();
  const isAdmin = me?.user.is_admin ?? false;
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState<Page | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPage(null);
    void get<Page>(`/api/customers/${customer.name}/revenue`)
      .then(setPage)
      .catch((e) => setError(e.message));
  }, [customer.name]);

  const month = params.get("m") ? `${params.get("m")}-01` : (page?.months[0]?.month ?? "");

  if (error) return <div className="error">{error}</div>;
  if (!page) return <div className="empty">Loading…</div>;

  const pick = (m: string) => {
    const next = new URLSearchParams(params);
    if (m === page.months[0]?.month) next.delete("m");
    else next.set("m", monthParam(m));
    setParams(next, { replace: true });
  };

  return (
    <div>
      <PlanCard plans={page.plans} isAdmin={isAdmin} slug={customer.name} />

      {page.months.length === 0 ? (
        <div className="empty">
          No platform tenant is mapped to this customer, so there is nothing to price.
        </div>
      ) : (
        <MonthTable months={page.months} selected={month} onPick={pick} />
      )}

      {month &&
        (month === page.months[0]?.month && page.months[0]?.status === "live" ? (
          <PaceCard slug={customer.name} />
        ) : (
          <BriefCard slug={customer.name} month={month} />
        ))}

      {month && <MonthStatement slug={customer.name} month={month} />}
    </div>
  );
}

/** The plan in effect today, and its history. */
function PlanCard({ plans, isAdmin, slug }: { plans: BillingPlanRow[]; isAdmin: boolean; slug: string }) {
  const [showAll, setShowAll] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const current = plans
    .filter((p) => p.effective_from <= today)
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from) || b.version - a.version)[0];

  return (
    <div className="rev-plan">
      <div className="controls" style={{ justifyContent: "space-between", margin: 0 }}>
        <div>
          <strong>Billing plan</strong>{" "}
          {current ? (
            <span className="dim">
              v{current.version} · from {monthLabel(current.effective_from)} · {current.currency}
              {current.note && ` · ${current.note}`}
            </span>
          ) : (
            <span className="rev-warn">
              None entered — every month is estimated at the default rate.
            </span>
          )}
        </div>
        <div className="fgroup">
          {plans.length > 0 && (
            <button className="btn tiny" onClick={() => setShowAll((v) => !v)}>
              {showAll ? "Hide history" : `History (${plans.length})`}
            </button>
          )}
          {isAdmin && (
            <Link className="btn tiny" to={`/c/${slug}/revenue/plan`}>
              {current || plans.length ? "Edit plan" : "Enter plan"}
            </Link>
          )}
        </div>
      </div>
      {current && <PlanSummary plan={current.plan} currency={current.currency} />}
      {showAll && (
        <ul className="dim">
          {plans.map((p) => (
            <li key={p.id}>
              v{p.version} — from {monthLabel(p.effective_from)}, {p.currency}, saved by{" "}
              {p.created_by_name ?? "unknown"} <Time value={p.created_at} />
              {p.note && ` — ${p.note}`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function callRate(r: string | number, currency: string): string {
  return `${currency === "INR" ? "₹" : "RM "}${Number(r).toFixed(2)}/call`;
}

export function PlanSummary({ plan, currency, reached }: { plan: Plan; currency: string; reached?: string }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div className="dim">AI voice bot: {describePulse(plan.bot.pulse)}</div>
      <div className="rev-ladder">
        {plan.bot.tiers.map((t) => (
          <span key={t.label} className={t.label === reached ? "on" : ""}>
            {t.label} · {threshold(t.from_minutes)} · {rate(t.rate_per_min, currency)}
            {Object.entries(t.rate_per_call ?? {}).map(([k, v]) => ` · ${ruleName(k)} ${callRate(v, currency)}`)}
          </span>
        ))}
      </div>
      <div className="dim">
        Dialer:{" "}
        {plan.dialer
          ? `${describePulse(plan.dialer.pulse)}; ${plan.dialer.tiers
              .map((t) => `${t.label} ${threshold(t.from_minutes)} ${rate(t.rate_per_min, currency)}`)
              .join(", ")}`
          : "not billed"}
      </div>
      {plan.overrides.length > 0 && (
        <div className="dim">
          Overrides:{" "}
          {plan.overrides
            .map((o) => o.label || ("campaign" in o.match ? "a campaign" : "a domain"))
            .join(", ")}
        </div>
      )}
    </div>
  );
}

function MonthTable({
  months,
  selected,
  onPick,
}: {
  months: MonthFigures[];
  selected: string;
  onPick: (m: string) => void;
}) {
  return (
    <div className="ctable rev-months">
      <table aria-label="Revenue by month">
        <thead>
          <tr>
            <th>Month</th>
            <th>Tier</th>
            <th className="n" title="Billable AI-bot minutes after pulse rounding">
              Bot min
            </th>
            <th className="n">VM</th>
            <th className="n">Dialer min</th>
            <th className="n">Bot</th>
            <th className="n">Dialer</th>
            <th className="n">Total</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {months.map((f, i) => (
            <tr
              key={f.month}
              className={f.month === selected ? "on" : ""}
              onClick={() => onPick(f.month)}
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && onPick(f.month)}
            >
              <td>
                {monthLabel(f.month)} {i === 0 && <span className="dim">to date</span>}
              </td>
              <td>
                {f.status === "not_computed" ? "—" : f.tier_label}
                {f.projection && f.projection.tier_label !== f.tier_label && (
                  <span className="dim"> → {f.projection.tier_label}</span>
                )}
                {f.below_mmc_minutes !== null && (
                  <div className="rev-warn">
                    {minutes(Number(f.below_mmc_minutes) * 60)} min below commitment
                  </div>
                )}
              </td>
              <td className="n">{minutes(f.bot_billable_secs)}</td>
              <td className="n">{vmShare(f)}</td>
              <td className="n">{f.dialer_billable_secs ? minutes(f.dialer_billable_secs) : "—"}</td>
              <td className="n">{money(f.bot_amount, f.currency || "INR")}</td>
              <td className="n">{Number(f.dialer_amount) ? money(f.dialer_amount, f.currency) : "—"}</td>
              <td className="n">
                <strong title={money(f.total_amount, f.currency || "INR", true)}>
                  {money(f.total_amount, f.currency || "INR")}
                </strong>
                {f.projection && (
                  <div className="dim" title="Projected month-end at the current run-rate">
                    ~{money(f.projection.total_amount, f.currency)}
                  </div>
                )}
              </td>
              <td>
                <StatusPill status={f.status} />
                {f.estimated && f.status !== "not_computed" && (
                  <span className="pill rev-est" title="No plan in effect; priced at the default rate">
                    Est.
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type GroupBy = "domain" | "campaign" | "group";

/** One month in full: the charge lines, the tier ladder and every line item. */
function MonthStatement({ slug, month }: { slug: string; month: string }) {
  const [detail, setDetail] = useState<RevenueMonthDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [by, setBy] = useState<GroupBy>("campaign");

  useEffect(() => {
    setDetail(null);
    setError(null);
    void get<RevenueMonthDetail>(`/api/customers/${slug}/revenue/${monthParam(month)}`)
      .then(setDetail)
      .catch((e) => setError(e.message));
  }, [slug, month]);

  if (error) return <div className="error">{error}</div>;
  if (!detail) return <div className="empty">Loading {monthLabel(month)}…</div>;

  const f = detail.figures;
  if (f.status === "not_computed") {
    return (
      <div className="rev-statement">
        <h3>{monthLabel(month)}</h3>
        <p className="dim">
          The mirror holds nothing for this month. An admin can fill it with{" "}
          <code>make revenue-month M={monthParam(month)}</code>.
        </p>
      </div>
    );
  }
  const cur = f.currency;
  const base = detail.groups.find((g) => g.key === "customer") ?? detail.groups[0];

  return (
    <div className="rev-statement">
      <h3>
        {monthLabel(month)} <StatusPill status={f.status} />
      </h3>
      <p className="dim">
        {f.status === "frozen" && f.computed_at && (
          <>
            Frozen <Time value={f.computed_at} /> ({f.source}
            {detail.frozen_by_name && ` by ${detail.frozen_by_name}`}
            {detail.reason && `: ${detail.reason}`}).{" "}
          </>
        )}
        {f.estimated
          ? "Estimated at the default rate: no plan was in effect."
          : `Priced under plan v${f.plan_version}.`}
      </p>

      <div className="ctable">
        <table aria-label="Charges">
          <thead>
            <tr>
              <th>Charge</th>
              <th className="n">Billed</th>
              <th className="n">Rate</th>
              <th className="n">Amount</th>
            </tr>
          </thead>
          <tbody>
            <ChargeRow
              label={`AI voice bot · connected${f.bot_flat_calls ? " (per minute)" : ""}`}
              quantity={`${minutes(perMinute(detail.lines, "connected", "bot"))} min`}
              rateText={detail.groups.length > 1 ? "by group" : rate(base?.bot_rate, cur)}
              amount={minuteAmount(detail.lines, "connected", "bot")}
              currency={cur}
            />
            <ChargeRow
              label={`AI voice bot · voicemail${f.bot_flat_calls ? " (per minute)" : ""}`}
              quantity={`${minutes(perMinute(detail.lines, "voicemail", "bot"))} min`}
              rateText={detail.groups.length > 1 ? "by group" : rate(base?.bot_rate, cur)}
              amount={minuteAmount(detail.lines, "voicemail", "bot")}
              currency={cur}
            />
            {f.bot_flat_calls > 0 && (
              <ChargeRow
                label="AI voice bot · per call"
                quantity={`${new Intl.NumberFormat("en-IN").format(f.bot_flat_calls)} calls`}
                rateText={callRates(base?.bot_call_rates, detail.groups.length, cur)}
                amount={Number(f.bot_flat_amount)}
                currency={cur}
              />
            )}
            <ChargeRow
              label={`Dialer${f.dialer_flat_calls ? " (per minute)" : ""}`}
              quantity={`${minutes(f.dialer_flat_calls ? perMinute(detail.lines, null, "dialer") : f.dialer_billable_secs)} min`}
              rateText={base?.dialer_rate ? rate(base.dialer_rate, cur) : "not billed"}
              amount={Number(f.dialer_amount) - Number(f.dialer_flat_amount)}
              currency={cur}
            />
            {f.dialer_flat_calls > 0 && (
              <ChargeRow
                label="Dialer · per call"
                quantity={`${new Intl.NumberFormat("en-IN").format(f.dialer_flat_calls)} calls`}
                rateText={callRates(base?.dialer_call_rates ?? undefined, detail.groups.length, cur)}
                amount={Number(f.dialer_flat_amount)}
                currency={cur}
              />
            )}
            <tr>
              <td>
                <strong>Total</strong>
              </td>
              <td />
              <td />
              <td className="n">
                <strong>{money(f.total_amount, cur, true)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {detail.plan && <PlanSummary plan={detail.plan} currency={cur} reached={f.tier_label} />}
      {f.projection && (
        <p className="dim">
          At this month's run-rate: {minutes(Number(f.projection.bot_billable_minutes) * 60)} billable
          minutes, {f.projection.tier_label}, {money(f.projection.total_amount, cur)}.
        </p>
      )}
      {detail.groups.length > 1 && (
        <div className="dim">
          Pricing groups:{" "}
          {detail.groups
            .map((g) => `${g.label} — ${g.bot_tier} ${rate(g.bot_rate, cur)}, ${minutes(g.bot_billable_secs)} min`)
            .join("; ")}
        </div>
      )}

      <div className="controls rev-lines">
        <span className="dim">Breakdown by</span>
        <div className="picker" role="group" style={{ margin: 0 }}>
          {(["campaign", "domain", "group"] as const).map((g) => (
            <button key={g} className={by === g ? "on" : ""} aria-pressed={by === g} onClick={() => setBy(g)}>
              {g === "group" ? "pricing group" : g}
            </button>
          ))}
        </div>
      </div>
      <Breakdown lines={detail.lines} by={by} currency={cur} />

      {detail.history.length > 0 && (
        <details>
          <summary className="dim">Earlier snapshots of this month ({detail.history.length})</summary>
          <ul className="dim">
            {detail.history.map((h) => (
              <li key={h.statement_id}>
                {h.computed_at && <Time value={h.computed_at} />} ({h.source}) — {h.tier_label},{" "}
                {money(h.total_amount, h.currency, true)}
                {h.estimated && " (estimated)"}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** Seconds charged per minute, for one kind (or every kind). */
function perMinute(lines: RevenueLine[], kind: RevenueLine["kind"] | null, charge: "bot" | "dialer"): number {
  return lines
    .filter((l) => kind === null || l.kind === kind)
    .reduce((a, l) => a + (charge === "bot" ? l.bot_per_minute_secs : l.dialer_per_minute_secs), 0);
}

/** The per-minute part of one kind's amount: the line total less its flat part. */
function minuteAmount(lines: RevenueLine[], kind: RevenueLine["kind"], charge: "bot" | "dialer"): number {
  return lines
    .filter((l) => l.kind === kind)
    .reduce(
      (a, l) =>
        a +
        (charge === "bot"
          ? Number(l.bot_amount) - Number(l.bot_flat_amount)
          : Number(l.dialer_amount) - Number(l.dialer_flat_amount)),
      0,
    );
}

function callRates(rates: Record<string, string> | undefined, groups: number, currency: string): string {
  if (groups > 1) return "by group";
  const entries = Object.entries(rates ?? {});
  if (entries.length === 0) return "—";
  return entries.map(([k, v]) => `${ruleName(k)} ${callRate(v, currency)}`).join(", ");
}

function ChargeRow({
  label,
  quantity,
  rateText,
  amount,
  currency,
}: {
  label: string;
  quantity: string;
  rateText: string;
  amount: number;
  currency: string;
}) {
  return (
    <tr>
      <td>{label}</td>
      <td className="n">{quantity}</td>
      <td className="n">{rateText}</td>
      <td className="n">{money(amount, currency, true)}</td>
    </tr>
  );
}

/** Line items regrouped in the browser: the server sends one row per
    campaign and kind, keyed by its ids, so any split is a sum. */
function Breakdown({ lines, by, currency }: { lines: RevenueLine[]; by: GroupBy; currency: string }) {
  const rows = useMemo(() => {
    const acc = new Map<
      string,
      { label: string; sub: string; calls: number; raw: number; bot: number; vm: number; dialer: number; flat: number; amount: number }
    >();
    for (const l of lines) {
      const key =
        by === "campaign"
          ? `${l.region}:${l.campaign_id}`
          : by === "domain"
            ? `${l.region}:${l.domain_id}`
            : l.pricing_group;
      const label =
        by === "campaign"
          ? (l.campaign_name ?? l.campaign_id)
          : by === "domain"
            ? (l.domain_identifier ?? l.domain_id)
            : l.pricing_group === "customer"
              ? "Customer plan"
              : l.pricing_group;
      const sub = by === "campaign" ? `${l.domain_identifier ?? l.domain_id} · ${l.region}` : by === "domain" ? l.region : "";
      const r = acc.get(key) ?? { label, sub, calls: 0, raw: 0, bot: 0, vm: 0, dialer: 0, flat: 0, amount: 0 };
      r.flat += l.bot_flat_calls + l.dialer_flat_calls;
      r.calls += l.calls;
      r.raw += l.raw_secs;
      r.bot += l.bot_billable_secs;
      if (l.kind === "voicemail") r.vm += l.bot_billable_secs;
      r.dialer += l.dialer_billable_secs;
      r.amount += Number(l.bot_amount) + Number(l.dialer_amount);
      acc.set(key, r);
    }
    return [...acc.values()].sort((a, b) => b.amount - a.amount);
  }, [lines, by]);

  if (rows.length === 0) return <div className="empty">No billable calls this month.</div>;
  const anyFlat = rows.some((r) => r.flat > 0);

  return (
    <div className="ctable">
      <table aria-label="Breakdown">
        <thead>
          <tr>
            <th>{by === "group" ? "Pricing group" : by === "domain" ? "Domain" : "Campaign"}</th>
            <th className="n">Calls</th>
            <th className="n" title="Call length before any pulse">
              Talk min
            </th>
            <th className="n" title="After the bot pulse">
              Bot min
            </th>
            <th className="n">VM</th>
            <th className="n">Dialer min</th>
            {anyFlat && (
              <th className="n" title="Calls charged per call rather than per minute (bot and dialer)">
                Flat calls
              </th>
            )}
            <th className="n">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label + r.sub}>
              <td>
                {r.label}
                {r.sub && <div className="dim">{r.sub}</div>}
              </td>
              <td className="n">{new Intl.NumberFormat("en-IN").format(r.calls)}</td>
              <td className="n">{minutes(r.raw)}</td>
              <td className="n">{minutes(r.bot)}</td>
              <td className="n">{vmShare({ voicemail_billable_secs: r.vm, bot_billable_secs: r.bot })}</td>
              <td className="n">{r.dialer ? minutes(r.dialer) : "—"}</td>
              {anyFlat && <td className="n">{r.flat ? new Intl.NumberFormat("en-IN").format(r.flat) : "—"}</td>}
              <td className="n">{money(r.amount, currency, true)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
